import { WPImage, WPLink } from "../index";
import { ContentBuilderType } from "./content-builder-types";

export type ACFNullable<T> = false | T;

export type ACFNullableArray<T> = false | T[];

export const makeAcfNullableSafe = <T>(type: ACFNullable<T>): T | undefined => {
    if (type === false) {
        return undefined;
    }
    return type;
};

export type OtherSupportColumn = {
    title: string;
    description: string;
    buttons: { link: WPLink }[];
};

export type OtherSupportType = {
    title: string;
    columns: OtherSupportColumn[];
};

export type HomePageAcf = {
    homepage_hero_section: {
        hero_banner_image: WPImage;
        hero_banner_mobile_image: WPImage;
    };
    populations_section: { copy: string };
    // Currently nothing in this field group - ACF defaults to false in this case
    conditions_section: false;
    // Currently nothing in this field group - ACF defaults to false in this case
    challenging_situations_section: false;
    for_health_professionals: {
        title: string;
        copy: string;
        button: WPLink;
        image: WPImage;
    };
    other_support: OtherSupportType;
    notification_bar: {
        enabled: boolean;
        content: string;
    };
};

export type NSWTBProcessLocalServices = {
    mininum_age: string;
    maximum_age: string;
    gender: false | number; // gender term id
    specific_to_gender: boolean;
    communities: string[] | undefined;
    conditions: false | number[];
    active_community: string | undefined;
    lgbtq: boolean;
    atsi: boolean;
    multicultural: boolean;
    free: boolean;
};

export type SimpleLocalServices = {
    show_local_services_on_this_page: boolean;
    communities: number[];
    conditions: number[];
};

export type LocalServicesFields = {
    nswtb_process_local_services: boolean;
    local_services_description: false | string;
    local_services: NSWTBProcessLocalServices;
};

export type PopulationPostTypeACF = {
    meta?: {
        alternate_page_title: string;
        sentence_case_title: string;
        hero_image: WPImage;
    };
    content?: ContentBuilderType;
    enable_alternate_content: boolean;
    alternate_content?: ContentBuilderType;
} & LocalServicesFields;

export type ConditionPostTypeACF = {
    meta?: {
        alternate_page_title: string;
        sentence_case_title: string;
        hero_image: WPImage;
    };
    content?: ContentBuilderType;
    enable_alternate_content: boolean;
    alternate_content?: ContentBuilderType;
} & LocalServicesFields;

export type ChallengingSituationsACF = {
    meta?: {
        sentence_case_title: string;
        hero_image: WPImage;
    };
    content?: ContentBuilderType;
    enable_alternate_content: boolean;
    alternate_content?: ContentBuilderType;
} & LocalServicesFields;

export type LocalInformationPostTypeACF = {
    meta?: {
        hero_image: WPImage;
    };
    content?: ContentBuilderType;
};

export type LocalListingPostTypeACF = {
    meta?: {
        logo: WPImage;
    };
    content?: ContentBuilderType;
};

export type CorePostTypesAcf =
    | PopulationPostTypeACF
    | ConditionPostTypeACF
    | ChallengingSituationsACF;

export type AllPostTypesAcf =
    | PopulationPostTypeACF
    | ConditionPostTypeACF
    | LocalInformationPostTypeACF;

export type PagePostTypeAcf = {
    content: ContentBuilderType;
};

export type NeedHelpNowCard = {
    title: string;
    copy: string;
    logo: WPImage;
    logo_background_colour: string;
    phone: {
        number: string;
        hours: string;
    };
    chat: {
        link?: string;
        hours?: string;
    };
};

export type PageTemplateINeedHelpAcf = {
    content: {
        title: string;
        copy: string;
        cards: NeedHelpNowCard[] | false;
    };
    hero: {
        title: string;
        subtitle: string;
        button_text: string;
    };
};

export type PageTemplateDirectoryAcf = {
    meta: {
        hero_image: WPImage;
    };
};

export interface AcfAddress {
    address: string;
    lat: number;
    lng: number;
}

export interface ListingLocation {
    name: string;
    service_provided_by: string;
    additional_info: string;
    location_email: string | false | null;
    phone_number: string | false | null;
    address_title: string;
    address: AcfAddress;
    location_website: string | undefined;
    services_at_this_location: [];
}

export type AgeRangeType = {
    minimum: string;
    maximum: string;
};

// Gender expressions on the frontend have two options that equate to the
// 'different-identity' value on the backend, hence the separate FE/BE types.
export enum GenderExpressionFE {
    Male = "male",
    Female = "female",
    NonBinaryOrGenderFluid = "nbgf",
    DifferentIdentity = "other",
}

export const genderExpressionFEToString = (gender: GenderExpressionFE | undefined) => {
    switch (gender) {
        case GenderExpressionFE.Male:
            return "Male";
        case GenderExpressionFE.Female:
            return "Female";
        case GenderExpressionFE.NonBinaryOrGenderFluid:
            return "Non binrary or gender fluid";
        case GenderExpressionFE.DifferentIdentity:
            return "Other";
    }
};

export enum ReferralOption {
    SelfReferral = "self-referral",
    GPReferral = "gp-referral",
    NotApplicable = "not-applicable",
}

export const getCopyForReferralOption = (option: ReferralOption | false): string | undefined => {
    switch (option) {
        case ReferralOption.GPReferral:
            return "GP referral";
        case ReferralOption.SelfReferral:
            return "Self-referral";
        case ReferralOption.NotApplicable:
            return "Not applicable";
    }
};

export enum CostOfService {
    Free = "free",
    NotApplicable = "not-applicable",
}

export const getCopyForCostOfService = (option: CostOfService | false): string | undefined => {
    switch (option) {
        case CostOfService.Free:
            return "Free service";
        case CostOfService.NotApplicable:
            return "Not applicable";
    }
};

export type CommunityDescription = {
    community: number;
    description: string;
};

export type ListingAcf = {
    location_toggle: string;
    also_available_by_phone_or_online: boolean;
    meta: {
        featured: boolean;
        logo: WPImage;
        logo_background_colour: string;
        blurb: string;
        phone_number: string;
        email_address: string;
        website: string;
        referral: ACFNullable<ReferralOption>;
        cost_of_service: ACFNullable<CostOfService>;
        service_provided_by: string;
        minimum_age: string;
        maximum_age: string;
        communities: ACFNullable<number[]>;
        communities_descriptions: ACFNullable<CommunityDescription[]>;
        conditions_and_needs: ACFNullable<number[]>;
        genders: ACFNullable<number[]>;
        is_exclusive: boolean;
        support_types: string;
    };
    content: {
        description: string;
    };
    locations: ACFNullable<ListingLocation[]>;
};

// Exactly the same as ListingAcf, but with locations removed
export type OtherSupportAcf = Omit<ListingAcf, "locations">;